/**
 * The resource group properties.
 */
export interface ResourceGroupProperties {
    /**
     * The provisioning state.
     */
    provisioningState: string;
}

/**
 * Resource group information.
 */
export interface ResourceGroup {
    /**
     * The ID of the resource group.
     */
    id: string;

    /**
     * The location of the resource group. It cannot be changed after the resource group has been created.
     * It must be one of the supported Azure locations.
     */
    location: string;

    /**
     * The ID of the resource that manages this resource group.
     */
    managedBy: string;

    /**
     * The name of the resource group.
     */
    name: string;

    /**
     * The resource group properties.
     */
    properties: ResourceGroupProperties;

    /**
     * The tags attached to the resource group.
     * As per Azure documentation - https://docs.microsoft.com/en-us/rest/api/resources/resourcegroups/createorupdate
     * tags is object. This field is read and retrieved as it is saved and never interpreted.
     */
    tags: any;

    /**
     * The type of the resource group.
     */
    type: string;
}

/**
 * List of resource groups.
 */
export interface ResourceGroupListResult {
    /**
     * The URL to use for getting the next set of results.
     */
    nextLink: string;

    /**
     * An array of resource groups.
     */
    value: ResourceGroup[];
}

/**
 * Custom response for listing all available resource groups.
 */
export interface ResourceGroupsOperationResult {
    /**
     * The http status code.
     */
    httpStatusCode: number;

    /**
     * The subscription list.
     */
    value?: ResourceGroupListResult;
}

/**
 * Custom response for create, update or get one single resource group.
 */
export interface ResourceGroupOperationResult {
    /**
     * The http status code.
     */
    httpStatusCode: number;

    /**
     * The subscription list.
     */
    value?: ResourceGroup;
}

/**
 * Request body to create a resource group.
 */
export interface ResourceGroupRequestBody {
    /**
     * The location of the resource group. It cannot be changed after the resource group has been created.
     * It must be one of the supported Azure locations.
     */
    location: string;

    /**
     * The ID of the resource that manages this resource group.
     */
    managedBy?: string;

    /**
     * The resource group properties.
     */
    properties?: ResourceGroupProperties;

    /**
     * The tags attached to the resource group.
     */
    tags?: any;
}
